import { useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useFetch, SectionCard, StatusChip, fmtDate } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FestivalPhonePreview } from "@/components/FestivalPhonePreview";
import { BannerUpload } from "@/components/BannerUpload";

const BLANK = { name: "", greeting: "", primary_color: "", accent_color: "", banner_image_url: "", starts_at: "", ends_at: "", enabled: true };
const TONE = { LIVE: "green", UPCOMING: "blue", ENDED: "gray", DISABLED: "amber" };
const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function FestForm({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid sm:grid-cols-4 gap-3">
      <label className="text-xs">Festival name<Input value={value.name} onChange={set("name")} placeholder="Diwali" data-testid="fest-name" /></label>
      <label className="text-xs">Greeting<Input value={value.greeting} onChange={set("greeting")} placeholder="Happy Diwali!" data-testid="fest-greeting" /></label>
      <label className="text-xs">Primary colour<Input value={value.primary_color} onChange={set("primary_color")} placeholder="#E65100" data-testid="fest-primary" /></label>
      <label className="text-xs">Accent colour<Input value={value.accent_color} onChange={set("accent_color")} placeholder="#FFD54F" data-testid="fest-accent" /></label>
      <div className="text-xs sm:col-span-2">Banner image (upload or paste an https link)<Input value={value.banner_image_url} onChange={set("banner_image_url")} data-testid="fest-banner" />
        <BannerUpload value={value.banner_image_url} onChange={(url) => onChange({ ...value, banner_image_url: url })} /></div>
      <label className="text-xs">Starts<Input type="datetime-local" value={value.starts_at} onChange={set("starts_at")} data-testid="fest-starts" /></label>
      <label className="text-xs">Ends<Input type="datetime-local" value={value.ends_at} onChange={set("ends_at")} data-testid="fest-ends" /></label>
    </div>
  );
}

function payload(f) {
  const out = { ...f, starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : null, ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null };
  ["greeting", "primary_color", "accent_color", "banner_image_url"].forEach((k) => { if (!out[k]) out[k] = null; });
  return out;
}

export function FestiveSchedule({ onChanged, base }) {
  const { data, refetch } = useFetch("/admin/app-theme/festivals", []);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const done = () => { setForm(BLANK); setEditId(null); refetch(); onChanged?.(); };
  const save = async () => {
    try {
      if (editId) await api.put(`/admin/app-theme/festivals/${editId}`, payload(form));
      else await api.post("/admin/app-theme/festivals", payload(form));
      toast.success(editId ? "Festival updated" : "Festival scheduled"); done();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const edit = (r) => { setEditId(r.id); setForm({ ...BLANK, ...Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v ?? ""])), starts_at: toLocal(r.starts_at), ends_at: toLocal(r.ends_at), enabled: r.enabled }); document.querySelector('[data-testid="festive-schedule"]')?.scrollIntoView({ behavior: "smooth" }); };
  const toggle = async (r) => {
    try { await api.put(`/admin/app-theme/festivals/${r.id}`, { ...payload({ ...BLANK, ...Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v ?? ""])), starts_at: toLocal(r.starts_at), ends_at: toLocal(r.ends_at) }), enabled: !r.enabled }); toast.success(r.enabled ? "Festival disabled" : "Festival enabled"); done(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete festive theme "${r.name}"?`)) return;
    try { await api.delete(`/admin/app-theme/festivals/${r.id}`); toast.success("Deleted"); done(); } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <SectionCard className="p-5 mt-6" data-testid="festive-schedule">
      <h3 className="font-semibold font-outfit mb-1">Festival schedule</h3>
      <p className="text-xs text-muted-foreground mb-3">Each festival switches on automatically at its start time and off at its end time. Dates cannot overlap.</p>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <FestForm value={form} onChange={setForm} />
          <div className="flex gap-2 mt-3">
            <Button onClick={save} data-testid="fest-save">{editId ? "Update festival" : "Add festival"}</Button>
            {editId && <Button variant="outline" onClick={() => { setForm(BLANK); setEditId(null); }} data-testid="fest-cancel">Cancel</Button>}
          </div>
        </div>
        <FestivalPhonePreview fest={form} base={base} />
      </div>
      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="fest-table">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-2">Festival</th><th>Starts</th><th>Ends</th><th>Colours</th><th>Status</th><th /></tr></thead>
          <tbody>
            {(data?.items || []).map((r) => (
              <tr key={r.id} className="border-t" data-testid={`fest-row-${r.id}`}>
                <td className="py-2 font-medium">{r.name}<div className="text-xs text-muted-foreground">{r.greeting}</div></td>
                <td>{fmtDate(r.starts_at)}</td><td>{fmtDate(r.ends_at)}</td>
                <td><div className="flex gap-1">{[r.primary_color, r.accent_color].filter(Boolean).map((c) => <span key={c} className="w-4 h-4 rounded-full border" style={{ background: c }} />)}</div></td>
                <td data-testid={`fest-state-${r.id}`}><StatusChip status={r.state} tone={TONE[r.state]} /></td>
                <td className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => edit(r)} data-testid={`fest-edit-${r.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle(r)} data-testid={`fest-toggle-${r.id}`}>{r.enabled ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(r)} data-testid={`fest-delete-${r.id}`}>Delete</Button>
                </td>
              </tr>
            ))}
            {!data?.items?.length && <tr><td colSpan={6} className="py-4 text-center text-xs text-muted-foreground" data-testid="fest-empty">No festivals scheduled yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
